/* Mirror the finished Zepto preview and the product's authoritative price.
   This controller never moves the original canvas, fields, or gallery. */
(() => {
  'use strict';
  if (window.BWStickyAtcLive) return;

  function mount(bar) {
    const root = bar?.closest('product-info[id^="MainProduct-"]');
    if (!root || bar.bwLivePreview) return;
    const sectionId = bar.dataset.sectionId;
    let disposed = false;
    let frame = 0;
    let readyCanvas = null;
    let hook = null;
    const thumbnails = new Set();

    function schedule() {
      if (!disposed && !frame) frame = requestAnimationFrame(sync);
    }

    function acceptRender(wrapper) {
      const element = wrapper?.jquery ? wrapper[0] : wrapper;
      const canvas = element?.querySelector?.('#pplr_canvas');
      // Print/export canvases and quick views must never replace this product.
      if (canvas && root.contains(canvas) && !canvas.closest('.pplr_crop-modal,.pplr_print_main')) {
        readyCanvas = canvas;
        schedule();
      }
    }

    function restoreHook() {
      if (hook && hook.owner[hook.key] === hook.wrapped) hook.owner[hook.key] = hook.original;
      hook = null;
    }

    function bindRenderHook() {
      // Zepto invokes syncAfterRender after committing a complete preview,
      // including cached view changes. Preserve its own interaction callback.
      const overlayHook = typeof window.PPLR_OV?.syncAfterRender === 'function';
      const owner = overlayHook ? window.PPLR_OV : window;
      const key = overlayHook ? 'syncAfterRender' : 'pplr_load_finish';
      if (typeof owner[key] !== 'function') return;
      if (hook?.owner === owner && hook.key === key && owner[key] === hook.wrapped) return;
      restoreHook();
      const original = owner[key];
      const wrapped = function () {
        const result = original.apply(this, arguments);
        if (!disposed) acceptRender(arguments[0]);
        return result;
      };
      owner[key] = wrapped;
      hook = { owner, key, original, wrapped };
    }

    function appReady() {
      bindRenderHook();
      // Late mounting is safe only after Zepto finished its current render.
      if (root.querySelector('.product-personalizer.pplr_image_loaded') && window.pplrloading === false) {
        acceptRender(root.querySelector('.pplr_preview_wrapper'));
      }
      schedule();
    }

    function syncPrice() {
      const mainPrice = root.querySelector(`[id="price-${sectionId}"] .price`);
      const source = mainPrice?.querySelector('.price__container');
      if (!source) return;
      const checked = root.querySelector('.bs-form-check-input:checked');
      const quantityTotal = checked && (
        checked.closest('.lumin-product-block-qty-break')?.querySelector('[class*="lumin-product-block-qty-break-total"]') ||
        root.querySelector('[class*="lumin-product-block-qty-break-total"]')
      );
      // Retain configured quantity-break totals; otherwise follow the actual
      // product price, which Zepto formats and updates (also when text clears).
      const content = quantityTotal?.innerHTML.trim() ? quantityTotal : source;
      const fragment = content.cloneNode(true);
      fragment.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      const html = fragment.innerHTML;
      bar.querySelectorAll('.sticky-atc-price .price').forEach(price => {
        const container = price.querySelector('.price__container');
        if (container && container.innerHTML !== html) container.innerHTML = html;
        ['price--on-sale', 'price--sold-out', 'price--no-compare', 'volume-pricing--sale-badge'].forEach(name => {
          price.classList.toggle(name, mainPrice.classList.contains(name));
        });
      });
    }

    function syncThumbnail() {
      const source = readyCanvas;
      if (!source?.isConnected || !root.contains(source) || !source.width || !source.height) {
        thumbnails.forEach(canvas => { canvas.hidden = true; });
        return;
      }
      bar.querySelectorAll('.sticky-atc-image').forEach(container => {
        let thumbnail = container.querySelector('.bw-sticky-live-preview');
        if (!thumbnail) {
          thumbnail = document.createElement('canvas');
          thumbnail.className = 'bw-sticky-live-preview';
          // The existing image supplies the product's accessible description.
          thumbnail.setAttribute('aria-hidden', 'true');
          thumbnail.hidden = true;
          container.appendChild(thumbnail);
          thumbnails.add(thumbnail);
        }
        // Draw at thumbnail resolution, without exporting full-size data URLs.
        // drawImage also works when an external image taints the source canvas.
        const width = 128;
        const height = Math.max(1, Math.round(width * source.height / source.width));
        try {
          const context = thumbnail.getContext('2d');
          if (!context) return;
          if (thumbnail.width !== width) thumbnail.width = width;
          if (thumbnail.height !== height) thumbnail.height = height;
          context.clearRect(0, 0, width, height);
          context.drawImage(source, 0, 0, width, height);
          thumbnail.hidden = false;
        } catch (_) {
          // The selected variant image stays visible if a preview is unavailable.
          thumbnail.hidden = true;
        }
      });
      thumbnails.forEach(canvas => { if (!canvas.isConnected) thumbnails.delete(canvas); });
    }

    function sync() {
      frame = 0;
      if (disposed) return;
      syncPrice();
      syncThumbnail();
    }

    function inputChanged(event) {
      if (event.target.matches('input[name="id"]')) {
        readyCanvas = null;
      }
      schedule();
    }

    // Watch only this product. Ignore our own price/thumbnail writes, while
    // rehydrating when the theme replaces the sticky bar's variant markup.
    const observer = new MutationObserver(records => {
      if (records.some(record => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (target === bar) return record.type === 'childList';
        if (bar.contains(target)) return false;
        return target?.closest(`[id="price-${sectionId}"],.lumin-product-block-qty-break`) ||
          (record.type === 'childList' && Array.from(record.addedNodes).some(node =>
            node.nodeType === 1 && (node.matches('[id^="price-"],.lumin-product-block-qty-break') ||
              node.querySelector('[id^="price-"],.lumin-product-block-qty-break'))));
      })) schedule();
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class'] });

    function scriptLoaded(event) {
      if (event.target.tagName === 'SCRIPT' && /product-personalizer\//.test(event.target.src)) appReady();
    }
    function sectionUnloaded(event) {
      if (event.target === root || event.target.contains(root)) cleanup();
    }
    function cleanup() {
      if (disposed) return;
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      restoreHook();
      thumbnails.forEach(canvas => canvas.remove());
      thumbnails.clear();
      root.removeEventListener('input', inputChanged);
      root.removeEventListener('change', inputChanged);
      window.removeEventListener('pplrAppBuild', appReady);
      window.removeEventListener('pplrAppInitiated', appReady);
      document.removeEventListener('load', scriptLoaded, true);
      document.removeEventListener('shopify:section:unload', sectionUnloaded);
      delete bar.bwLivePreview;
    }
    root.addEventListener('input', inputChanged);
    root.addEventListener('change', inputChanged);
    window.addEventListener('pplrAppBuild', appReady);
    window.addEventListener('pplrAppInitiated', appReady);
    document.addEventListener('load', scriptLoaded, true);
    document.addEventListener('shopify:section:unload', sectionUnloaded);
    bar.bwLivePreview = { cleanup };
    appReady();
  }

  function mountAll(scope = document) {
    scope.querySelectorAll('.sticky-atc').forEach(mount);
  }
  window.BWStickyAtcLive = { mount };
  document.addEventListener('shopify:section:load', event => mountAll(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mountAll(), { once: true });
  else mountAll();
})();
